import React, { useMemo, useState } from "react";
import ColumnFilterPopover, {
  ColumnFilterValue,
  matchesColumnFilter,
  isColumnFilterActive,
} from "./ColumnFilterPopover";

/**
 * Herhangi bir liste ekranındaki tabloya DevExpress tarzı sütun filtresi eklemek için
 * paylaşımlı yardımcılar. Kullanım:
 *   const defs = useMemo(() => [{ key:"firma", value:(r)=>r.firmName }, ...], [deps]);
 *   const { ctx, filtered } = useTableColumnFilters(rows, defs);
 *   <thead><tr><FilterTh label="Firma" colKey="firma" ctx={ctx} /> ...
 *   {filtered.map(...)}
 */

const NUMERIC = ["int", "float", "decimal", "currency"];

export interface ColDef<T> {
  key: string;
  type?: string; // string(default) | int | float | decimal | currency | datetime | bool
  value: (r: T) => any; // ham değer (karşılaştırma için)
  display?: (r: T) => string; // görünen metin (checkbox listesi + eşleşme); yoksa value string'i
}

export interface TableFilterCtx {
  filters: Record<string, ColumnFilterValue>;
  setFilter: (k: string, v?: ColumnFilterValue) => void;
  options: Record<string, string[]>;
  hasActive: boolean;
  clearAll: () => void;
}

export function useTableColumnFilters<T>(
  rows: T[],
  defs: ColDef<T>[],
): { ctx: TableFilterCtx; filtered: T[] } {
  const [filters, setFilters] = useState<Record<string, ColumnFilterValue>>({});

  const disp = (d: ColDef<T>, r: T): string => {
    if (d.display) return d.display(r);
    const v = d.value(r);
    return v == null ? "" : String(v);
  };

  const byKey = useMemo(() => {
    const m: Record<string, ColDef<T>> = {};
    defs.forEach((d) => (m[d.key] = d));
    return m;
  }, [defs]);

  const options = useMemo(() => {
    const m: Record<string, string[]> = {};
    for (const d of defs) {
      const t = d.type || "string";
      if (NUMERIC.includes(t) || t === "datetime") continue;
      const s = new Set<string>();
      for (const r of rows) {
        const v = disp(d, r);
        if (v && v !== "-") s.add(v);
      }
      m[d.key] = Array.from(s).sort((a, b) => a.localeCompare(b, "tr"));
    }
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, defs]);

  const filtered = useMemo(() => {
    const act = Object.entries(filters).filter(([, v]) => isColumnFilterActive(v));
    if (!act.length) return rows;
    return rows.filter((r) =>
      act.every(([k, f]) => {
        const d = byKey[k];
        if (!d) return true;
        return matchesColumnFilter(disp(d, r), d.value(r), f, d.type || "string");
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, filters, byKey]);

  const setFilter = (k: string, v?: ColumnFilterValue) =>
    setFilters((p) => {
      const n = { ...p };
      if (v) n[k] = v;
      else delete n[k];
      return n;
    });

  const hasActive = Object.values(filters).some(isColumnFilterActive);

  return { ctx: { filters, setFilter, options, hasActive, clearAll: () => setFilters({}) }, filtered };
}

export const FilterTh: React.FC<{
  label: string;
  colKey: string;
  type?: string;
  ctx: TableFilterCtx;
  className?: string;
  align?: "left" | "right";
  style?: React.CSSProperties;
}> = ({ label, colKey, type = "string", ctx, className, align, style }) => (
  <th className={className} style={style}>
    <div className="cf-th">
      <span className="cf-th-label">{label}</span>
      <ColumnFilterPopover
        dataType={type}
        options={ctx.options[colKey] || []}
        value={ctx.filters[colKey]}
        align={align || (NUMERIC.includes(type) ? "right" : "left")}
        onChange={(v) => ctx.setFilter(colKey, v)}
      />
    </div>
  </th>
);

/** Hem tıkla-sırala hem filtre içeren başlık hücresi (mevcut sıralamayı korur). */
export const SortFilterTh: React.FC<{
  label: string;
  sortKey: string;
  colKey: string;
  type?: string;
  sortConfig: { key: string; direction: string } | any;
  onSort: (k: string) => void;
  ctx: TableFilterCtx;
  className?: string;
}> = ({ label, sortKey, colKey, type = "string", sortConfig, onSort, ctx, className }) => (
  <th className={className ? `${className} sortable-header` : "sortable-header"} onClick={() => onSort(sortKey)} style={{ cursor: "pointer" }}>
    <div className="cf-th">
      <span className="cf-th-label">
        {label}
        {sortConfig?.key === sortKey && (
          <span className="sort-indicator">{sortConfig.direction === "asc" ? " ↑" : " ↓"}</span>
        )}
      </span>
      <ColumnFilterPopover
        dataType={type}
        options={ctx.options[colKey] || []}
        value={ctx.filters[colKey]}
        align={NUMERIC.includes(type) ? "right" : "left"}
        onChange={(v) => ctx.setFilter(colKey, v)}
      />
    </div>
  </th>
);
